import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { request, requestVoid, toSlotDto, type Warning } from '@/shared/api'
import type { Interval } from '@/shared/lib'
import {
  ScheduleSchema,
  type Task,
  type TaskDraft,
  type TaskPatch,
  TaskResultSchema,
} from '../model/schema'

export const tasksQueryKey = ['tasks'] as const

export interface ScheduleQuery {
  from: Date
  to: Date
  assigneeIds?: string[]
  includeUntimed: boolean
}

export interface Schedule {
  items: Task[]
  untimed: Task[]
}

export function scheduleQueryOptions(params: ScheduleQuery) {
  const query = {
    from: params.from.toISOString(),
    to: params.to.toISOString(),
    assignee_ids: params.assigneeIds ?? [],
    include_untimed: params.includeUntimed,
  }
  return queryOptions({
    queryKey: [...tasksQueryKey, query] as const,
    queryFn: (): Promise<Schedule> => request('/schedule', ScheduleSchema, { query }),
  })
}

export interface TaskResult {
  task: Task
  warnings: Warning[]
}

function slotBody(slot: Interval | null) {
  return slot === null ? null : toSlotDto(slot)
}

export function useCreateTaskMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (draft: TaskDraft): Promise<TaskResult> =>
      request('/tasks', TaskResultSchema, {
        method: 'POST',
        body: {
          title: draft.title,
          slot: slotBody(draft.slot),
          assignees: draft.assigneeIds,
          ...(draft.excursionId ? { excursion_id: draft.excursionId } : {}),
          note: draft.note,
        },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: tasksQueryKey }),
  })
}

/**
 * Одна ручка на всю правку и отметку: карточка меняет время и исполнителей
 * разом — двумя запросами это дало бы промежуточное состояние в сетке у
 * остальных.
 *
 * Tri-state: поле отсутствует — не трогать, `null` — очистить.
 */
export function useUpdateTaskMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TaskPatch }): Promise<TaskResult> =>
      request(`/tasks/${id}`, TaskResultSchema, {
        method: 'PATCH',
        body: {
          ...(patch.title === undefined ? {} : { title: patch.title }),
          ...(patch.slot === undefined ? {} : { slot: slotBody(patch.slot) }),
          ...(patch.assigneeIds === undefined ? {} : { assignees: patch.assigneeIds }),
          ...(patch.excursionId === undefined ? {} : { excursion_id: patch.excursionId }),
          ...(patch.note === undefined ? {} : { note: patch.note }),
          ...(patch.done === undefined ? {} : { done: patch.done }),
        },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: tasksQueryKey }),
  })
}

export function useDeleteTaskMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string): Promise<void> => requestVoid(`/tasks/${id}`, { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: tasksQueryKey }),
  })
}
